import React from "react";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Link from "next/link";
import {
    EcommerceEvent,
    EcommerceEventList,
    ReservedTicketList,
} from "../models/Event";
import { getImageUrl } from "../utils/helpers";
import { observer } from "mobx-react-lite";
import AuthStore from "../stores/AuthStore";
import dynamic from "next/dynamic";

const SessionWidget = dynamic(() => import("./SessionWidget"), { ssr: false });

const liStyles = {
    marginBottom: "10px",
    padding: "10px",
    borderBottom: "1px solid #ddd",
};

const ClientHeader = observer(
    ({
        eventList,
        baseUrl,
        overrideLogo,
    }: {
        eventList: EcommerceEventList | EcommerceEvent | ReservedTicketList;
        baseUrl?: string;
        overrideLogo?: string;
    }) => {
        const [open, setOpen] = React.useState(false);
        const [gsOpen, setGsOpen] = React.useState(false);

        const onClose = () => {
            setOpen(false);
        };

        const onGsClose = () => {
            setGsOpen(false);
        };

        const getLogo = () => {
            if (overrideLogo) {
                return getImageUrl(overrideLogo);
            }
            return eventList.logoUrl
                ? getImageUrl(eventList.logoUrl)
                : "https://cdn.authentigate.ca/clients/28/orange_flag_transparent.png";
        };

        return (
            <Box>
                <Grid container spacing={1} sx={{ my: 2 }}>
                    <Grid item xs={12} lg={3}>
                        <SessionWidget clientId={eventList.clientId} />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        lg={6}
                        sx={{
                            textAlign: "center",
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Box sx={{ width: "90%" }}>
                            <Link
                                href={baseUrl ?? `/store/${eventList.cartUrl}`}
                            >
                                <img
                                    src={getLogo()}
                                    alt={`${eventList.clientName} Logo`}
                                    style={{
                                        maxHeight: "90px",
                                        maxWidth: "90%",
                                        objectFit: "contain",
                                    }}
                                />
                            </Link>
                        </Box>
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: {
                                    xs: "center",
                                    lg: "flex-end",
                                },
                                justifyContent: {
                                    xs: "center",
                                },
                                gap: "5px",
                            }}
                        >
                            <Button
                                color="info"
                                onClick={() => {
                                    setOpen(true);
                                }}
                                variant="contained"
                                size="small"
                                sx={{
                                    mr: {
                                        lg: 8,
                                    },
                                }}
                            >
                                Contact Info
                            </Button>
                            {[102, 106].includes(eventList.clientId) && (
                                <Button
                                    color="secondary"
                                    onClick={() => {
                                        setGsOpen(true);
                                    }}
                                    variant="contained"
                                    size="small"
                                    sx={{
                                        mr: {
                                            lg: 8,
                                        },
                                    }}
                                >
                                    General Grandstand Info
                                </Button>
                            )}
                        </Box>
                    </Grid>
                </Grid>
                <Dialog open={open} onClose={onClose} fullWidth>
                    <DialogTitle
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Typography variant="h5">Contact Info</Typography>
                        <IconButton onClick={onClose}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Box
                            dangerouslySetInnerHTML={{
                                __html: eventList.customerServiceInfo,
                            }}
                        />
                    </DialogContent>
                </Dialog>

                <Dialog open={gsOpen} onClose={onGsClose} fullWidth>
                    <DialogTitle
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Typography variant="h5">
                            General Grandstand Info
                        </Typography>
                        <IconButton onClick={onGsClose}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Box>
                            <ul
                                style={{
                                    listStyleType: "none",
                                    padding: 0,
                                }}
                            >
                                <li style={liStyles}>
                                    <span
                                        style={{
                                            fontWeight: "bold",
                                            color: "#9c2626",
                                        }}
                                    >
                                        All ticket sales are final.
                                    </span>{" "}
                                    There are no exchanges or returns.
                                </li>
                                <li style={liStyles}>
                                    All seating is assigned.
                                </li>
                                <li style={liStyles}>
                                    Child pricing is in effect for those 12 &
                                    under.
                                </li>
                                <li style={liStyles}>
                                    There is no access to the race track, rodeo
                                    arena or infield seating/suites from the
                                    main grandstand or beer garden area and vice
                                    versa during performances. Infield Access is
                                    available from the West Tunnel entrance or
                                    Southeast Tunnel entrance with the proper
                                    tickets.
                                </li>
                                <li style={liStyles}>
                                    No pets allowed in the grandstand. Service
                                    animals with proper Service Animal ID &
                                    documentation are welcome.
                                </li>
                                <li style={liStyles}>
                                    No smoking / vaping in the grandstand or
                                    beer garden area. A designated smoking area
                                    is located within the beer gardens.
                                </li>
                                <li style={liStyles}>
                                    No alcohol is to be brought into or taken
                                    out of the fenced grandstand or beer garden
                                    areas.
                                </li>
                                <li style={liStyles}>
                                    No strollers in the grandstand seating area.
                                    Stroller storage is available at the
                                    user&apos;s risk.
                                </li>
                            </ul>
                            <div
                                style={{
                                    marginTop: "20px",
                                    padding: "15px",
                                    backgroundColor: "#ecf0f1",
                                    borderRadius: "5px",
                                }}
                            >
                                <Typography variant="h6">
                                    Accessibility Seating
                                </Typography>
                                <p>
                                    Accessible entrances are located on the West
                                    and East ends of the main grandstand.
                                    Assigned accessible seating is sold via the
                                    Ponoka Stampede box office (
                                    <a href="tel:4037830100">403-783-0100</a>).
                                </p>
                                <p>
                                    Due to space limitations, one companion seat
                                    is sold with each accessible space seat.
                                </p>
                            </div>
                        </Box>
                    </DialogContent>
                </Dialog>
            </Box>
        );
    },
);

export default ClientHeader;
